/**
 * WHAT AN ORGANIZER MAY NOT SAY ON THE SIGN-IN PAGE (step 2 of the
 * conference-supplied sign-in screen, 2026-10-13; the owner: "do step2").
 *
 * WHY. A conference somebody made in Revent may put its own heading and line
 * on robutler.ai's sign-in page (lib/apps/revent-sign-in-screens.ts, the
 * organizer case). Those words show under the Robutler mark, beside our
 * provider buttons, on the one page where a person decides to trust us with
 * an account. That is the best place on the platform to phish from: "Verify
 * your account at robutler-login.co", "Enter the code we texted you", "Call
 * +1 415 555 0100 for your passcode". The operator's own words (step 1) are
 * not judged here, and nothing else on the page changes: the mark, the
 * buttons and the label under the mark stay ours.
 *
 * WHAT IS REFUSED, and the words are refused rather than cleaned, because a
 * screen with a hole cut in it is still the organizer's sentence:
 *   - link: a web address of any kind (a scheme, `www.`, a bare domain such
 *     as `example.co`, "dot com" written out);
 *   - email: an email address (also `[at]` written out);
 *   - phone: a phone number (a `+` and seven digits, ten digits in one run,
 *     or the `555-0100` shape);
 *   - credential: words that ask for what an account is opened with:
 *     password, passcode, PIN, one-time code or OTP, verification code, seed
 *     or recovery phrase, card number (and its security code), bank details,
 *     "verify your account", "log in at", "sign in at".
 * Matched after NFKC folding (full-width and circled letters, full-width
 * digits and `＠`, and the ideographic full stops browsers read as a dot in
 * an address), with the invisible characters taken out, and case-blind,
 * except PIN, which is refused as the capitalised word or beside "code" or
 * "number" so "Pin the talks you like" and "Pinterest" stand. "Sign in" alone
 * stands too: the screen IS a sign-in. The list is short and readable on
 * purpose; it is a filter in front of staff take-down
 * (app/api/admin/sign-in-screens/[conference]/takedown/route.ts), not a
 * replacement for it.
 *
 * ONE PLACE. This module is the authority; the reader refuses a screen whose
 * words break any rule (a refused screen shows nothing custom: the app's
 * default screen draws, never half of the organizer's). Revent's editor
 * (public/widgets/revent/lib/sign-in-rules.js) carries a literal copy of the
 * patterns so an organizer is told before saving, and
 * tests/unit/auth/sign-in-text-rules.test.ts holds the two equal. Plain
 * module, no imports, client-safe.
 */

export type SignInTextRule = 'link' | 'email' | 'phone' | 'credential';

/** The rules, in the order the editor reports them. */
export const SIGN_IN_TEXT_RULES: readonly SignInTextRule[] = ['link', 'email', 'phone', 'credential'];

/* The invisible characters cleanSignInText strips (lib/auth/published-sign-in.ts),
   built from code points for the same reason: a literal of them in source is
   itself a hazard. Stripped again here so the rules hold on any input. */
const ch = (n: number) => String.fromCharCode(n);
const span = (a: number, b: number) => `${ch(a)}-${ch(b)}`;
const INVISIBLE_RE = new RegExp(
  `[${span(0x00, 0x08)}${span(0x0b, 0x1f)}${span(0x7f, 0x9f)}${ch(0xad)}${span(0x200b, 0x200f)}${span(0x2028, 0x202e)}${span(0x2060, 0x206f)}${ch(0xfeff)}]`,
  'g',
);
/* The full stops an address bar reads as a dot (IDNA): ideographic, full-width, half-width. */
const DOT_RE = new RegExp(`[${ch(0x3002)}${ch(0xff0e)}${ch(0xff61)}]`, 'g');

/**
 * The patterns, each against the folded text (`fold`). `cased` patterns run
 * on the folded text before it is lower-cased (PIN is a word only in
 * capitals). The bundle's copy spells the same sources; the test compares
 * them as strings.
 */
export const SIGN_IN_TEXT_PATTERNS: ReadonlyArray<{ rule: SignInTextRule; re: RegExp; cased?: true }> = [
  // A scheme with its slashes, a scheme that needs none, `www.`.
  { rule: 'link', re: /\b[a-z][a-z0-9+.-]{1,20}:\/\//u },
  { rule: 'link', re: /\b(?:mailto|tel|sms|javascript|data|whatsapp|tg|skype|callto):/u },
  { rule: 'link', re: /\bwww\s*\./u },
  // A bare domain: labels of letters and digits joined by dots, ending in an
  // ASCII top-level name (or its punycode), not glued to more of the word.
  { rule: 'link', re: /(?:^|[^\p{L}\p{N}@._-])(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,61}[\p{L}\p{N}])?\.)+(?:[a-z]{2,24}|xn--[a-z0-9-]{1,59})(?![\p{L}\p{N}-])/u },
  // An address spelled out: "example dot com", "example [dot] com".
  { rule: 'link', re: /\bdot\s+(?:com|net|org|io|ai|co|app|xyz|me|link|info|biz)\b|[[(]\s*dot\s*[\])]/u },
  { rule: 'email', re: /[\p{L}\p{N}._%+-]+@[\p{L}\p{N}-]+(?:\.[\p{L}\p{N}-]+)+/u },
  { rule: 'email', re: /[[(]\s*at\s*[\])]/u },
  // A `+` and at least seven digits in one run; ten digits in one run; 555-0100.
  { rule: 'phone', re: /\+\s*\(?\d(?:[\s().-]*\d){6,}/u },
  { rule: 'phone', re: /\d(?:[\s().-]*\d){9,}/u },
  { rule: 'phone', re: /(?<!\d)\d{3}[-.]\d{4}(?!\d)/u },
  { rule: 'credential', re: /\bpass\s*-?\s*(?:word|code|phrase)s?\b/u },
  { rule: 'credential', re: /\bPINs?\b/u, cased: true },
  { rule: 'credential', re: /\bpin\s*(?:code|number|no\b)/u },
  { rule: 'credential', re: /\botps?\b|\bone\s*-?\s*time\s*(?:code|pass(?:word|code)?|pin|login)s?\b/u },
  { rule: 'credential', re: /\b(?:verification|security|confirmation)\s*codes?\b|\bcvv\b|\bcvc\b/u },
  { rule: 'credential', re: /\b(?:seed|recovery|secret)\s*(?:phrase|words?)s?\b/u },
  { rule: 'credential', re: /\bcard\s*(?:number|no\b|#)/u },
  { rule: 'credential', re: /\bbank\s*(?:details|account|login)s?\b/u },
  { rule: 'credential', re: /\bverify\s+(?:your|ur)\s+(?:account|identity|wallet|login)\b/u },
  { rule: 'credential', re: /\b(?:log|sign)\s*-?\s*in\s+at\b/u },
];

/** The text the patterns read: NFKC folded, invisible characters out, address dots as dots. */
export function foldSignInText(text: string): string {
  return text.normalize('NFKC').replace(INVISIBLE_RE, '').replace(DOT_RE, '.');
}

/** The rules a text breaks, in `SIGN_IN_TEXT_RULES` order; empty when it may be shown. Not text answers none: the parser refuses it before this is asked. */
export function signInTextRefusals(text: unknown): SignInTextRule[] {
  if (typeof text !== 'string') return [];
  const folded = foldSignInText(text);
  const lower = folded.toLowerCase();
  const hit = new Set<SignInTextRule>();
  for (const p of SIGN_IN_TEXT_PATTERNS) if (!hit.has(p.rule) && p.re.test(p.cased ? folded : lower)) hit.add(p.rule);
  return SIGN_IN_TEXT_RULES.filter((r) => hit.has(r));
}

/** The rules any language of a screen's words breaks: one string, or one per language. */
export function signInWordsRefusals(words: unknown): SignInTextRule[] {
  const texts =
    typeof words === 'string'
      ? [words]
      : words && typeof words === 'object' && !Array.isArray(words)
        ? Object.values(words as Record<string, unknown>)
        : [];
  const hit = new Set<SignInTextRule>();
  for (const t of texts) for (const r of signInTextRefusals(t)) hit.add(r);
  return SIGN_IN_TEXT_RULES.filter((r) => hit.has(r));
}
